const SECRET_KEY = /authorization|cookie|token|secret|password|passwd|api[-_]?key|credentials|session|imap|^(body|requestBody|responseBody|prompt)$/i;
const REDACTED = '[redacted]';
/** Used at every telemetry boundary, including breadcrumbs and local diagnostics. */
export function sanitizeTelemetry(value) {
    const seen = new WeakSet();
    function visit(item, depth) {
        if (typeof item === 'string') {
            return item
                .replace(/\b(Bearer|Basic)\s+[a-z0-9._~+/=-]+/gi, `$1 ${REDACTED}`)
                .replace(/\b(password|passwd|token|secret|api[-_]?key)\s*[=:]\s*["']?[^\s&,;"']+/gi, `$1=${REDACTED}`)
                .replace(/https?:\/\/[^\s<>"')]+/g, (url) => {
                try {
                    const parsed = new URL(url);
                    parsed.username = '';
                    parsed.password = '';
                    parsed.search = '';
                    parsed.hash = '';
                    return parsed.href;
                }
                catch {
                    return '[invalid URL]';
                }
            })
                .slice(0, 16_000);
        }
        if (item === null || typeof item !== 'object')
            return item;
        if (depth > 10)
            return '[truncated]';
        if (seen.has(item))
            return '[circular]';
        seen.add(item);
        const entries = item instanceof Error
            ? Object.entries({ ...item, name: item.name, message: item.message, stack: item.stack, cause: item.cause })
            : Object.entries(item);
        const result = Array.isArray(item)
            ? item.slice(0, 100).map((entry) => visit(entry, depth + 1))
            : Object.fromEntries(entries.slice(0, 100).map(([key, entry]) => [key, SECRET_KEY.test(key) ? REDACTED : visit(entry, depth + 1)]));
        seen.delete(item);
        return result;
    }
    return visit(value, 0);
}
export function prepareTelemetryEvent(event) {
    const clean = sanitizeTelemetry(event);
    // GlitchTip accepts error envelopes without Sentry's tracing extensions.
    if (clean.contexts)
        delete clean.contexts.trace;
    if (clean.sdkProcessingMetadata)
        delete clean.sdkProcessingMetadata.dynamicSamplingContext;
    return clean;
}
/**
 * The dev server's own outages: the local API stopped or restarting (fetch failures,
 * the Vite proxy's 502/503/504 on api calls and diagnostics delivery, live updates giving up)
 * and Vite HMR reload errors.
 * Only meaningful for a dev build; production keeps reporting all of these.
 */
export function isLocalDevOutage(event) {
    const values = event.exception?.values ?? [];
    const texts = [event.message ?? '', ...values.map((entry) => `${entry.type ?? ''}: ${entry.value ?? ''}`)];
    if (texts.some((text) => /^\[vite\] /.test(text) || /Live updates could not reconnect/.test(text)))
        return true;
    return values.length > 0 && values.every((entry) => (entry.type === 'TypeError' && /^(Failed to fetch|NetworkError when attempting to fetch resource|Load failed)\b/.test(entry.value ?? ''))
        || entry.type === 'TuyauNetworkError'
        || (entry.type === 'Error' && /^Client diagnostics rejected \(50[234]\)$/.test(entry.value ?? ''))
        || (/^(Tuyau)?HTTPError$/.test(entry.type ?? '') && /status code 50[234]\b/.test(entry.value ?? '')));
}
/**
 * Level of a failed app API call. The session probe (`auth.me`) answering 401
 * is how a signed-out visitor is detected, so it stays in the trail only.
 */
export function apiErrorLevel(route, status) {
    if (route === 'auth.me' && status === 401)
        return 'info';
    return status >= 500 || status === 0 ? 'error' : 'warn';
}
/** Signals from Reddit's own scripts, beacons and assets (sessions inject no scripts). */
const PAGE_CONTEXT = new Set(['page.console', 'page.exception', 'request.failed', 'response.failed']);
/** Browser automation emits serialized outcomes as well as thrown errors. */
export function browserDiagnosticLevel(event, data) {
    // Page context for the trail, not automation failures; a failed document load still is.
    if (PAGE_CONTEXT.has(event) && data?.type !== 'document')
        return 'info';
    const result = data?.result;
    const status = result && typeof result === 'object' && 'status' in result ? result.status : data?.status;
    if (/\.error$|exception|crashed|hung/i.test(event) || status === 'failed' || status === 'uncertain' || (typeof status === 'number' && status >= 500) || (event === 'page.console' && data?.type === 'error'))
        return 'error';
    if (/failed|drift|challenge|refused|blocked|console|pageerror|http_error/i.test(event) || (typeof status === 'number' && status >= 400))
        return 'warn';
    return event.endsWith('.start') ? 'debug' : 'info';
}
/** Resource and chunk failures do not reach the SDK's window.onerror handler. */
export function observeBrowserFailures(report) {
    const resourceError = (event) => {
        const target = event.target;
        if (!(target instanceof Element))
            return;
        const source = target.getAttribute('src') ?? target.getAttribute('href');
        if (!source)
            return;
        report({ name: 'resource.error', error: new Error(`Failed to load ${target.tagName.toLowerCase()}`), data: { resource: source, element: target.tagName } });
    };
    const preloadError = (event) => {
        const payload = 'payload' in event ? event.payload : undefined;
        report({ name: 'chunk.error', error: payload ?? new Error('Failed to load application chunk') });
    };
    window.addEventListener('error', resourceError, true);
    window.addEventListener('vite:preloadError', preloadError);
    return () => {
        window.removeEventListener('error', resourceError, true);
        window.removeEventListener('vite:preloadError', preloadError);
    };
}
//# sourceMappingURL=monitoring.js.map